import { useState, useMemo } from "react";
import { Loader2, Search, Eye, Download, CheckSquare, Square, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useFurstImportSearch,
  useFurstImportExecute,
  type FurstSearchResult,
  type FurstImportResult,
} from "@/hooks/api/useFurstImport";

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return { from: formatDate(from), to: formatDate(to) };
}

function statusBadge(status: string) {
  const labels: Record<string, string> = {
    FE: "Ferdig",
    ER: "Endelig rapport",
    UA: "Under arbeid",
  };
  const variant =
    status === "FE" ? ("default" as const) :
    status === "ER" ? ("secondary" as const) :
    ("outline" as const);
  return <Badge variant={variant}>{labels[status] || status}</Badge>;
}

function labVisitStatusBadge(status: string) {
  return <Badge variant="outline">{status}</Badge>;
}

export function FurstImportTab() {
  const defaults = defaultDateRange();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [furstCookie, setFurstCookie] = useState("");
  const [searchResults, setSearchResults] = useState<FurstSearchResult[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewResults, setPreviewResults] = useState<FurstImportResult[] | null>(null);
  const [importResults, setImportResults] = useState<FurstImportResult[] | null>(null);

  const search = useFurstImportSearch();
  const importExecute = useFurstImportExecute();

  const aminoResults = useMemo(
    () => searchResults.filter((r) => r.isAminoUser),
    [searchResults]
  );

  const handleSearch = async () => {
    if (!furstCookie.trim()) {
      toast.error("Furst cookie er påkrevd");
      return;
    }
    try {
      const results = await search.mutateAsync({
        furstCookie,
        fromDate,
        toDate,
      });
      setSearchResults(results);
      setSelectedIds(new Set());
      setPreviewResults(null);
      setImportResults(null);
    } catch (err: any) {
      toast.error(err.message || "Søk feilet");
    }
  };

  const toggleSelect = (proveId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(proveId)) next.delete(proveId);
      else next.add(proveId);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(aminoResults.map((r) => r.proveId)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const getSelectedRequisitions = () =>
    searchResults
      .filter((r) => selectedIds.has(r.proveId) && r.isAminoUser && r.authUserId)
      .map((r) => ({
        proveId: r.proveId,
        authUserId: r.authUserId!,
        proveTattDato: r.proveTattDato,
        proveStatus: r.proveStatus,
      }));

  const handlePreview = async () => {
    const reqs = getSelectedRequisitions();
    if (reqs.length === 0) {
      toast.error("Velg minst én rekvisisjon");
      return;
    }
    try {
      const results = await importExecute.mutateAsync({
        furstCookie,
        dryRun: true,
        requisitions: reqs,
      });
      setPreviewResults(results);
      setImportResults(null);
    } catch (err: any) {
      toast.error(err.message || "Forhåndsvisning feilet");
    }
  };

  const handleImport = async () => {
    const reqs = getSelectedRequisitions();
    if (reqs.length === 0) {
      toast.error("Velg minst én rekvisisjon");
      return;
    }
    try {
      const results = await importExecute.mutateAsync({
        furstCookie,
        dryRun: false,
        requisitions: reqs,
      });
      setImportResults(results);
      setPreviewResults(null);
      const success = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;
      toast.success(`Import fullført: ${success} OK, ${failed} feilet`);
    } catch (err: any) {
      toast.error(err.message || "Import feilet");
    }
  };

  const isAllSelected =
    aminoResults.length > 0 &&
    aminoResults.every((r) => selectedIds.has(r.proveId));

  return (
    <div className="space-y-4 mt-4">
      {/* Search form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Søk i Furst</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3 items-end">
            <div>
              <label className="text-sm text-admin-text-secondary">Fra dato</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-admin-text-secondary">Til dato</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button onClick={handleSearch} disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Search className="h-4 w-4 mr-1" />
              )}
              Søk
            </Button>
          </div>
          <div>
            <label className="text-sm text-admin-text-secondary">Furst cookie</label>
            <textarea
              className="w-full mt-1 bg-admin-bg border border-admin-card-border rounded px-3 py-2 text-sm font-mono min-h-[60px]"
              value={furstCookie}
              onChange={(e) => setFurstCookie(e.target.value)}
              placeholder="Lim inn Furst session cookie..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Search results */}
      {searchResults.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Resultater ({searchResults.length})
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={isAllSelected ? deselectAll : selectAll}
                >
                  {isAllSelected ? (
                    <><Square className="h-3 w-3 mr-1" /> Fjern alle</>
                  ) : (
                    <><CheckSquare className="h-3 w-3 mr-1" /> Velg alle Amino</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreview}
                  disabled={selectedIds.size === 0 || importExecute.isPending}
                >
                  {importExecute.isPending && !importResults ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Eye className="h-3 w-3 mr-1" />
                  )}
                  Forhåndsvisning
                </Button>
                <Button
                  size="sm"
                  onClick={handleImport}
                  disabled={selectedIds.size === 0 || importExecute.isPending}
                >
                  {importExecute.isPending && !previewResults ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Download className="h-3 w-3 mr-1" />
                  )}
                  Importer
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Pasient</TableHead>
                  <TableHead>Dato</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amino-bruker</TableHead>
                  <TableHead>Eksisterende besøk</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searchResults.map((r) => (
                  <TableRow
                    key={r.proveId}
                    className={!r.isAminoUser ? "opacity-40" : undefined}
                  >
                    <TableCell>
                      {r.isAminoUser && (
                        <button
                          onClick={() => toggleSelect(r.proveId)}
                          className="cursor-pointer"
                        >
                          {selectedIds.has(r.proveId) ? (
                            <CheckSquare className="h-4 w-4 text-admin-primary" />
                          ) : (
                            <Square className="h-4 w-4 text-admin-text-secondary" />
                          )}
                        </button>
                      )}
                    </TableCell>
                    <TableCell>{r.patientName || "Ukjent"}</TableCell>
                    <TableCell className="text-sm">{r.proveTattDato}</TableCell>
                    <TableCell>{statusBadge(r.proveStatus)}</TableCell>
                    <TableCell>
                      {r.isAminoUser ? (
                        <Badge variant="default">Ja</Badge>
                      ) : (
                        <Badge variant="outline">Nei</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.existingLabVisit ? (
                        <span className="text-sm">
                          {labVisitStatusBadge(r.existingLabVisit.status)}
                        </span>
                      ) : (
                        <span className="text-admin-text-secondary text-sm">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Preview results */}
      {previewResults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Forhåndsvisning (dry run)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ImportResultsTable results={previewResults} />
          </CardContent>
        </Card>
      )}

      {/* Import results */}
      {importResults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Importresultater</CardTitle>
          </CardHeader>
          <CardContent>
            <ImportSummary results={importResults} />
            <ImportResultsTable results={importResults} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ImportSummary({ results }: { results: FurstImportResult[] }) {
  const success = results.filter((r) => r.success);
  const failed = results.filter((r) => !r.success);
  const created = success.filter((r) => r.labVisitCreated);
  const totalResults = success.reduce((sum, r) => sum + (r.resultsImported || 0), 0);

  return (
    <div className="flex gap-4 mb-4 text-sm">
      <div>
        <span className="text-admin-text-secondary">Importert:</span>{" "}
        <span className="font-medium">{success.length}</span>
      </div>
      <div>
        <span className="text-admin-text-secondary">Nye besøk:</span>{" "}
        <span className="font-medium">{created.length}</span>
      </div>
      <div>
        <span className="text-admin-text-secondary">Resultater:</span>{" "}
        <span className="font-medium">{totalResults}</span>
      </div>
      {failed.length > 0 && (
        <div>
          <span className="text-admin-text-secondary">Feilet:</span>{" "}
          <span className="font-medium text-admin-danger-400">{failed.length}</span>
        </div>
      )}
    </div>
  );
}

function matchMethodBadge(method: string) {
  const labels: Record<string, string> = { nlk: "NLK", furstId: "Furst ID", name: "Navn" };
  const variant =
    method === "nlk" ? ("default" as const) :
    method === "furstId" ? ("secondary" as const) :
    ("outline" as const);
  return <Badge variant={variant} className="text-[10px] px-1 py-0">{labels[method] || method}</Badge>;
}

function skipReasonLabel(reason: string) {
  const labels: Record<string, string> = {
    incomplete: "Ufullstendig (IF)",
    non_numeric: "Ikke-numerisk",
    unmapped: "Ukjent analyse",
  };
  return labels[reason] || reason;
}

function ImportResultsTable({ results }: { results: FurstImportResult[] }) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (proveId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(proveId)) next.delete(proveId);
      else next.add(proveId);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      {results.map((r) => {
        const isExpanded = expandedIds.has(r.proveId);
        const mapped = r.mappedBiometrics || [];
        const skippedList = r.skippedAnalyses || [];

        return (
          <div key={r.proveId} className="border border-admin-card-border rounded">
            {/* Summary row */}
            <button
              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-admin-bg/50 cursor-pointer"
              onClick={() => r.success && toggleExpand(r.proveId)}
            >
              {r.success ? (
                isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />
              ) : (
                <span className="w-4" />
              )}

              <span className="font-mono text-xs w-36 shrink-0">{r.proveId}</span>

              {r.success ? (
                <Badge variant="default" className="shrink-0">{r.dryRun ? "Preview OK" : "OK"}</Badge>
              ) : (
                <Badge variant="destructive" className="shrink-0">Feil</Badge>
              )}

              {r.success ? (
                <>
                  <span className="text-sm shrink-0">
                    {r.labVisitCreated ? (
                      "Nytt besøk"
                    ) : (
                      <>
                        <Badge variant="outline" className="text-[10px] px-1 py-0 mr-1">
                          {r.existingLabVisitStatus || "?"}
                        </Badge>
                        →
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-1">
                          {r.labVisitStatus || "?"}
                        </Badge>
                        {r.labVisitId && (
                          <span className="text-admin-text-secondary ml-1.5 font-mono text-[10px]">
                            {r.labVisitId.slice(0, 8)}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <span className="text-sm ml-auto shrink-0">
                    <span className="text-admin-success-400">{mapped.length} matchet</span>
                    {skippedList.length > 0 && (
                      <span className="text-admin-text-secondary ml-2">{skippedList.length} hoppet over</span>
                    )}
                  </span>
                </>
              ) : (
                <span className="text-admin-danger-400 text-xs">{r.error}</span>
              )}
            </button>

            {/* Expanded detail */}
            {isExpanded && r.success && (
              <div className="border-t border-admin-card-border px-3 py-2 space-y-3">
                {/* Matched biometrics table */}
                {mapped.length > 0 && (
                  <div>
                    <p className="text-xs text-admin-text-secondary mb-1 font-medium">
                      Matchede biomarkører ({mapped.length})
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Biomarkør</TableHead>
                          <TableHead className="text-xs">Furst-navn</TableHead>
                          <TableHead className="text-xs">Verdi</TableHead>
                          <TableHead className="text-xs">Enhet</TableHead>
                          <TableHead className="text-xs">Match</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mapped.map((m) => (
                          <TableRow key={m.biometricId}>
                            <TableCell className="text-sm font-medium">{m.biometricName}</TableCell>
                            <TableCell className="text-sm text-admin-text-secondary">{m.analyseNavn}</TableCell>
                            <TableCell className="text-sm font-mono">
                              {m.arithmetic === "less_than" && "<"}
                              {m.arithmetic === "greater_than" && ">"}
                              {m.value}
                            </TableCell>
                            <TableCell className="text-sm">{m.unit}</TableCell>
                            <TableCell>{matchMethodBadge(m.matchMethod)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Skipped analyses */}
                {skippedList.length > 0 && (
                  <div>
                    <p className="text-xs text-admin-text-secondary mb-1 font-medium">
                      Hoppet over ({skippedList.length})
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Analyse</TableHead>
                          <TableHead className="text-xs">Svar</TableHead>
                          <TableHead className="text-xs">Grunn</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {skippedList.map((s, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm">{s.analyseNavn}</TableCell>
                            <TableCell className="text-sm font-mono text-admin-text-secondary">{s.svar}</TableCell>
                            <TableCell>
                              <Badge
                                variant={s.reason === "unmapped" ? "destructive" : "outline"}
                                className="text-[10px] px-1 py-0"
                              >
                                {skipReasonLabel(s.reason)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
